import type { Sprite } from 'pixi.js';
import { lerp } from '../../shared/scalar';
import { ticksIntoBeat } from '../../shared/tempo';
import type { PropPhase, PropState, SimState } from '../../sim/state';
import { createBurster } from '../class-bursts';
import type { Family, RenderContext } from '../context';
import type { Frame } from '../frame';
import { type Shape, type Sheet, TAU, paintSheet, paintedLater } from '../paint';
import { fade } from '../player-look';
import { REFERENCE_RADIUS } from '../textures-looks';
import { add, byId, hide, lookup, setTint } from '../util';
import { ViewPool } from '../views';
import { CLUB_PIECES, CREAM, clubColor, clubsOf } from './jongleur-paint';

type ClubShapes = Readonly<Record<keyof typeof CLUB_PIECES, Shape>>;

const SPIN = { normal: 0.85, calm: 0.2 };
const LIFT = { flight: 9, hop: 16, home: 7 };
const RING = { radius: 15, alpha: 0.42, beat: 0.3 };
const WOBBLE = 0.35;
const GLOW = 18;

interface ClubView {
  readonly shadow: Sprite;
  readonly glow: Sprite;
  readonly ring: Sprite;
  readonly handle: Sprite;
  readonly body: Sprite;
}

interface Track {
  phase: PropPhase;
  since: number;
  target: number | undefined;
  fromX: number;
  fromY: number;
  x: number;
  y: number;
  seen: number;
}

export function createClubs(ctx: RenderContext): Family {
  const { layers, textures } = ctx;
  const sheet = paintedLater((): Sheet<keyof typeof CLUB_PIECES> => paintSheet(CLUB_PIECES));
  const burster = createBurster(ctx, 48);
  const tracks = new Map<number, Track>();
  let views: ViewPool<ClubView> | undefined;
  let image = 0;
  const forget = (track: Track, id: number): void => {
    if (track.seen !== image) {
      tracks.delete(id);
    }
  };

  function viewsOf(shapes: ClubShapes): ViewPool<ClubView> {
    views ??= new ViewPool<ClubView>(
      () => ({
        shadow: add(layers.weapons, shapes.clubShadow),
        ring: add(layers.weapons, textures.ring),
        glow: add(layers.glow, textures.halo),
        handle: add(layers.weapons, shapes.clubHandle),
        body: add(layers.weapons, shapes.clubBody),
      }),
      (view) => {
        hide(view.shadow, view.ring, view.glow, view.handle, view.body);
      },
    );
    return views;
  }

  function changed(state: SimState, club: PropState, track: Track, frame: Frame): void {
    const owner = byId(state.players, club.playerId);
    if (owner === undefined) {
      return;
    }
    const index = clubsOf(state, owner).indexOf(club);
    const color = clubColor(
      frame.palette,
      frame.palette[lookup(ctx.classTokens, owner.classId, 'class')],
      index,
    );
    const { now } = frame;
    if (club.phase === 'held' && (track.phase === 'grounded' || track.phase === 'returning')) {
      burster.spawn(frame, {
        shape: textures.ring,
        now,
        duration: 6,
        x: owner.x,
        y: owner.y,
        fromRadius: 10,
        toRadius: 26,
        tint: color,
        peak: 0.7,
      });
      if (club.boosted === true) {
        burster.spawn(frame, {
          shape: textures.ring,
          now,
          duration: 10,
          x: owner.x,
          y: owner.y,
          fromRadius: 16,
          toRadius: 46,
          tint: frame.palette.or,
          peak: 0.9,
        });
        for (let spark = 0; spark < (frame.calm ? 0 : 5); spark += 1) {
          const angle = (spark / 5) * TAU + now;
          burster.spawn(frame, {
            shape: textures.halo,
            now,
            duration: 7,
            x: owner.x + Math.cos(angle) * 12,
            y: owner.y + Math.sin(angle) * 12,
            fromRadius: 6,
            toRadius: 2,
            tint: frame.palette.or,
            peak: 0.9,
          });
        }
      }
    } else if (club.phase === 'grounded' && track.phase === 'flying') {
      burster.spawn(frame, {
        shape: textures.ring,
        now,
        duration: 5,
        x: club.x,
        y: club.y,
        fromRadius: 4,
        toRadius: 16,
        tint: frame.palette.texte,
        peak: 0.3,
      });
    } else if (club.phase === 'bouncing' && track.phase === 'grounded') {
      burster.spawn(frame, {
        shape: textures.ring,
        now,
        duration: 6,
        x: track.x,
        y: track.y,
        fromRadius: 14,
        toRadius: 4,
        tint: color,
        peak: 0.6,
      });
    }
  }

  function liftOf(state: SimState, club: PropState, track: Track, now: number): number {
    switch (club.phase) {
      case 'flying': {
        const done = now - track.since;
        return LIFT.flight * Math.sin(Math.PI * Math.min(1, done / (done + club.ticksLeft + 1)));
      }
      case 'bouncing': {
        const target = club.targetId === undefined ? undefined : byId(state.enemies, club.targetId);
        if (target === undefined) {
          return 0;
        }
        const gone = Math.hypot(club.x - track.fromX, club.y - track.fromY);
        const left = Math.hypot(target.x - club.x, target.y - club.y);
        return LIFT.hop * Math.sin(Math.PI * (gone / Math.max(1, gone + left)));
      }
      case 'returning':
        return LIFT.home;
      default:
        return 0;
    }
  }

  return {
    reset(): void {
      tracks.clear();
      burster.bursts.clear();
    },
    update(state: SimState, alpha: number, frame: Frame): void {
      const props = state.props;
      if (props === undefined && views === undefined) {
        return;
      }
      const { shapes } = sheet.get();
      const pool = viewsOf(shapes);
      const { now, calm, palette, light, pulse, tempo } = frame;
      image += 1;
      pool.begin();
      for (const club of props ?? []) {
        if (club.kind !== 'club') {
          continue;
        }
        let track = tracks.get(club.id);
        if (track === undefined) {
          track = {
            phase: club.phase,
            since: now,
            target: club.targetId,
            fromX: club.x,
            fromY: club.y,
            x: club.x,
            y: club.y,
            seen: image,
          };
          tracks.set(club.id, track);
        }
        if (track.phase !== club.phase) {
          changed(state, club, track, frame);
          track.phase = club.phase;
          track.since = now;
          track.fromX = club.prevX;
          track.fromY = club.prevY;
        }
        if (track.target !== club.targetId) {
          track.target = club.targetId;
          track.fromX = club.prevX;
          track.fromY = club.prevY;
        }
        track.x = club.x;
        track.y = club.y;
        track.seen = image;
        if (club.phase === 'held') {
          continue;
        }
        const owner = byId(state.players, club.playerId);
        if (owner === undefined) {
          continue;
        }
        const view = pool.acquire(club.id);
        const index = clubsOf(state, owner).indexOf(club);
        const color = clubColor(
          palette,
          palette[lookup(ctx.classTokens, owner.classId, 'class')],
          index,
        );
        const x = lerp(club.prevX, club.x, alpha);
        const y = lerp(club.prevY, club.y, alpha);
        const lift = liftOf(state, club, track, now);
        const size = owner.radius / REFERENCE_RADIUS;
        const grounded = club.phase === 'grounded';
        let turn = club.id * 2.4;
        if (!grounded) {
          turn += (now - track.since) * (calm ? SPIN.calm : SPIN.normal);
        } else if (!calm && club.ticksLeft < tempo.ticksPerBeat) {
          turn += WOBBLE * Math.sin(now * 1.7);
        }

        view.shadow.visible = !grounded;
        view.shadow.position.set(x + lift * 0.35, y + lift * 0.2);
        view.shadow.scale.set(size * (1 - lift / 60));
        view.shadow.rotation = turn;
        setTint(view.shadow, 0x000000);
        view.shadow.alpha = 0.22 * (1 - lift / 40);

        view.handle.visible = view.body.visible = true;
        for (const sprite of [view.handle, view.body]) {
          sprite.position.set(x, y - lift);
          sprite.rotation = turn;
          sprite.scale.set(size);
        }
        setTint(view.handle, CREAM);
        setTint(view.body, color);

        view.ring.visible = grounded;
        if (grounded) {
          const beat = calm ? 0 : fade(ticksIntoBeat(now, tempo), 3);
          view.ring.position.set(x, y);
          view.ring.scale.set((RING.radius * (1 + 0.12 * beat)) / textures.ring.radius);
          view.ring.alpha = RING.alpha + RING.beat * beat;
          setTint(view.ring, color);
        }
        view.glow.visible = !grounded;
        if (!grounded) {
          view.glow.position.set(x, y - lift);
          view.glow.scale.set(GLOW / textures.halo.radius);
          view.glow.alpha = light.haloAlpha * (0.8 + 0.2 * pulse);
          setTint(view.glow, color);
        }
      }
      pool.end();
      tracks.forEach(forget);
      burster.bursts.update(state, alpha, frame);
    },
    destroy(): void {
      burster.bursts.clear();
      sheet.painted?.source.destroy();
    },
  };
}
